/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationsListPage Component
 * ------------------------
 * Displays all applications associated with the logged-in organization.
 * Features:
 * 1. Supports pagination, creation, and navigation to individual application details.
 * 2. Integrates OAuth2 credential generation and viewing via ClientKeySecretDialog.
 * 3. Provides error and loading states with user-friendly messages.
 * 4. Allows regeneration of client credentials with proper security handling.
 *
 * Dependencies:
 * - APIHelper for backend API calls.
 * - ThemeContext for consistent UI theming.
 * - ClientKeySecretDialog for key/secret viewing and regeneration.
 * - MessageBox, Pagination, and Button for UI elements.
 * - useAlertDialog for displaying confirmation/error dialogs.
 */

import React, { useEffect, useState } from 'react';
import ApplicationCard from '../components/ApplicationCard';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from "../config/envConfig";
import MessageBox from '../components/MessageBox';
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';
import CredentialsManagerDialog from "../components/common/CredentialsManagerDialog";
import { useAlertDialog } from '../components/common/AlertDialogContext';
import Paginator from '../components/common/Paginator';
import ProgressBarDialog from '../components/common/ProgressBar';
import { useQuery } from '@tanstack/react-query';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const perPage = 18;

export default function ApplicationsListPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { showAlertDialog } = useAlertDialog();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [credManagerOpen, setCredManagerOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishProgressMessage, setPublishProgressMessage] = useState('');

  const [totalApplications, setTotalApplications] = useState(0);

  const applicationsQueryKey = ['applicationsList', page, perPage];

  const {
    data: applicationsQueryData,
    error: applicationsQueryError,
    isLoading: applicationsQueryLoading,
    isFetching: applicationsQueryFetching,
  } = useQuery({
    queryKey: applicationsQueryKey,
    queryFn: async ({ signal }) => {

      const resp = await apiHelper.apiCall(`/arcee/application/list/?page=${page}&page_size=${perPage}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal,
      });

      if (!resp.ok) {
        let j = {};
        try { j = await resp.json(); } catch (e) { /* ignore */ }
        throw new Error(j?.error || `Failed to fetch applications (${resp.status})`);
      }

      return await resp.json();
    },
    staleTime: 0,
    refetchOnMount: true,
    retry: 1,
  });

  useEffect(() => {
    setLoading(Boolean(applicationsQueryLoading || applicationsQueryFetching));

    if (applicationsQueryError) {
      setError(applicationsQueryError?.message || String(applicationsQueryError));
      setApplications([]);
      setTotalApplications(0);
      return;
    }

    if (applicationsQueryData) {
      setApplications(applicationsQueryData.results || []);
      setTotalApplications(applicationsQueryData.count || 0);
      setError(null);
    }
  }, [applicationsQueryData, applicationsQueryError, applicationsQueryLoading, applicationsQueryFetching]);

  const pageCount = Math.max(1, Math.ceil(totalApplications / perPage));
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
  };
  const handlePrevious = () => setPage(p => Math.max(p - 1, 1));
  const handleNext = () => setPage(p => Math.min(p + 1, pageCount));

  const handleOpen = (app) => { window.location = `/dashboard/applications/${app.id}`; };
  const handleEdit = (app) => { window.location = `/dashboard/applications/${app.id}/update`; };

  const handleDelete = (app) => {
    showAlertDialog({
      title: `Confirm Deletion`,
      message: `Are you sure you want to delete application "${app.name}"?`,
      type: "info",
      okLabel: "Delete",
      cancelLabel: "Cancel",
      showCancel: true,
      onOk: async () => {
        setShowProgress(true);
        setProgressMessage("Deleting application...");
        setDeleting(true);
        try {
          const resp = await apiHelper.apiCall('/arcee/application/delete/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ application_id: app.id }),
          });

          if (!resp.ok) {
            const errData = await resp.json();
            showAlertDialog({
              title: "Deletion Failed",
              message: errData.error || errData.message || errData.details || "An error occurred during deletion. Please try again later.",
              type: "error",
              okLabel: "Close",
              showCancel: false,
              onOk: () => window.location.reload()
            });
            return;
          }

          const data = await resp.json();

          if (data.status === "failed") {
            showAlertDialog({
              title: "Deletion Failed",
              message: data.message || "Failed to delete the application.",
              type: "error",
              okLabel: "Close",
              showCancel: false,
              onOk: () => window.location.reload()
            });
            return;
          }

          showAlertDialog({
            title: "Success",
            message: data.message || "Application deleted successfully.",
            type: "success",
            okLabel: "Close",
            showCancel: false,
            onOk: () => window.location.reload()
          });

        } catch (error) {
          console.error('Deletion error:', error);
          showAlertDialog({
            title: "Error",
            message: "An error occurred during deletion. Please try again later.",
            type: "error",
            okLabel: "Close",
            showCancel: false,
            onOk: () => window.location.reload()
          });
        } finally {
          setShowProgress(false);
          setDeleting(false);
        }
      },
      onCancel: () => {
        // Optional: clean-up or nothing on cancel
      }
    });
  };


  const handleGenerateCredentials = (app) => {
    setSelectedApp(app);
    setCredManagerOpen(true);
  };

  const handleCredManagerClose = () => {
    setCredManagerOpen(false);
    setSelectedApp(null);
  };

  const handleCreate = () => {
    navigate('/dashboard/applications/create');
  };

  const handlePublish = (app) => {
    showAlertDialog({
      title: 'Publish Application',
      message: `Are you sure you want to publish "${app.name}"?`,
      type: 'info',
      okLabel: 'Yes',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setPublishing(true);
        setPublishProgressMessage('Publishing application...');
        try {
          const resp = await apiHelper.apiCall('/arcee/application/publish/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ application_id: Number(app.id), status: 'published' }),
          });

          if (!resp.ok) {
            let errBody;
            try { errBody = await resp.json(); } catch { errBody = await resp.text().catch(() => null); }
            const message = (errBody && errBody.error) ? errBody.error : (typeof errBody === 'string' ? errBody : `Failed to publish ${app.name}.`);
            showAlertDialog({
              title: 'Publish failed',
              message,
              type: 'error',
              okLabel: 'OK',
              showCancel: false
            });
            return;
          }

          showAlertDialog({
            title: 'Success',
            message: `${app.name} published successfully.`,
            type: 'success',
            okLabel: 'OK',
            showCancel: false,
            onOk: () => {
              window.location.reload();
            }
          });
        } catch (err) {
          console.error('Publish error:', err);
          showAlertDialog({
            title: 'Network Error',
            message: err.message || 'Unable to contact server. Please try again later.',
            type: 'error',
            okLabel: 'OK',
            showCancel: false
          });
        } finally {
          setPublishing(false);
          setPublishProgressMessage('');
        }
      },
      onCancel: () => { /* nothing */ }
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh', color: theme.primary }}>
        <span className="spinner-border" role="status" aria-hidden="true"></span>
        <span className="ms-2">Loading...</span>
      </div>
    );
  }

  if (error) {
    const displayError = error || "Something went wrong. Please try again.";
    return (
      <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
        <MessageBox messageType="error" message={displayError} />
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div style={{ height: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', paddingTop: '10vh', position: 'relative', width: '100%' }}>
        <MessageBox messageType="info" message="No applications present. Please create one to get started." />
        <div style={{ position: 'relative', top: '10px' }}>
          <Button mode="primary-bold" onClick={handleCreate}>
            + Create
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center w-100" style={{ background: "#f8fafc", minHeight: '100vh', paddingBottom: '4rem' }}>

      {/* Hero Header */}
      <div className="w-100 px-4 py-5 mb-4" style={{ background: '#fff', borderBottom: '1px solid #eaeaea' }}>
        <div className="container-fluid" style={{ maxWidth: 1400 }}>
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1a1a1a', letterSpacing: '-0.5px' }}>
                Applications
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: '15px' }}>
                Manage your organization's applications and credentials
              </p>
            </div>
            <div>
              <Button
                mode="primary-bold"
                onClick={handleCreate}
                style={{ padding: '10px 24px', fontSize: '15px', borderRadius: '8px', boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
              >
                + Create Application
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Grid Content */}
      <div className="container-fluid px-4" style={{ maxWidth: 1400 }}>
        <div className="row g-4">
          {applications.map(app => (
            <div key={app.id} className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex">
              <ApplicationCard
                app={{
                  ...app,
                  title: app.name,
                  state: app.status,
                  logoUrl: app.logo,
                  description: app.description,
                  version: app.exported_as_pack ? app.exported_as_pack.version : (app.installed_from_pack ? app.installed_from_pack.version : undefined)
                }}
                onOpen={() => handleOpen(app)}
                onEdit={() => handleEdit(app)}
                onGenerateCredentials={() => handleGenerateCredentials(app)}
                onDelete={() => handleDelete(app)}
                showDelete={envConfig.showApplicationDelete}
                showInstall={false}
                showPublish={true}
                published={app.status === 'published'}
                onPublish={(a) => handlePublish(a)}
              />
            </div>
          ))}
        </div>

        <div className="mt-5">
          <Paginator
            page={page}
            pageCount={pageCount}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onPage={handlePageChange}
            maxPagesToShow={3}
          />
        </div>
      </div>

      <CredentialsManagerDialog
        open={credManagerOpen}
        onClose={handleCredManagerClose}
        application={selectedApp}
        apiHelper={apiHelper}
      />
      <ProgressBarDialog
        open={showProgress}
        text_message="Deleting Application..."
        cancellable={false}
      />
      <ProgressBarDialog
        open={publishing}
        text_message={publishProgressMessage || 'Publishing...'}
        cancellable={false}
      />
    </div>
  );
}
